import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, FileQuestion, Loader2 } from "lucide-react";
import { SpreadsheetPreview } from "@/components/SpreadsheetPreview";
import { PdfDocumentPreview } from "@/components/PdfDocumentPreview";
import {
  detectViewableKind,
  isIosSafari,
  officeViewerUrl,
  openUrlInNewTab,
  triggerDownload,
} from "@/lib/documentViewerUtils";

export interface ViewableDocument {
  url: string;
  fileName: string;
  fileType?: string | null;
}

interface DocumentViewerDialogProps {
  document: ViewableDocument | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const UnsupportedPanel = ({
  doc,
  message,
}: {
  doc: ViewableDocument;
  message: string;
}) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
    <FileQuestion className="h-10 w-10 text-muted-foreground" />
    <p className="text-sm text-muted-foreground max-w-sm">{message}</p>
    <div className="flex gap-2">
      <Button size="sm" onClick={() => triggerDownload(doc.url, doc.fileName)}>
        <Download className="h-4 w-4 mr-1" />
        Download
      </Button>
      <Button size="sm" variant="outline" onClick={() => openUrlInNewTab(doc.url)}>
        <ExternalLink className="h-4 w-4 mr-1" />
        Open in new tab
      </Button>
    </div>
  </div>
);

export const DocumentViewerDialog = ({ document: doc, open, onOpenChange }: DocumentViewerDialogProps) => {
  const kind = useMemo(
    () => (doc ? detectViewableKind(doc.fileType, doc.fileName) : "unsupported"),
    [doc],
  );

  const [textContent, setTextContent] = useState<string | null>(null);
  const [textError, setTextError] = useState<string | null>(null);
  const [officeFailed, setOfficeFailed] = useState(false);

  useEffect(() => {
    setOfficeFailed(false);
    setTextContent(null);
    setTextError(null);
    if (!doc || !open || kind !== "text") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(doc.url);
        if (!res.ok) throw new Error("Could not load file");
        const text = await res.text();
        if (!cancelled) setTextContent(text);
      } catch (e: any) {
        if (!cancelled) setTextError(e?.message || "Could not load file");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doc, open, kind]);

  const iosPdf = kind === "pdf" && isIosSafari();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[90dvh] max-h-[90dvh] max-w-6xl overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex flex-wrap items-center justify-between gap-2 pr-8">
            <span className="truncate max-w-full sm:max-w-[60%]">{doc?.fileName}</span>
            {doc && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openUrlInNewTab(doc.url)}>
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Open in new tab
                </Button>
                <Button size="sm" variant="outline" onClick={() => triggerDownload(doc.url, doc.fileName)}>
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </Button>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {!doc && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {doc && kind === "image" && (
            <img
              src={doc.url}
              alt={doc.fileName}
              className="w-full h-auto object-contain"
            />
          )}

          {doc && kind === "pdf" && (
            <div className="min-h-full w-full">
              {iosPdf ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Safari on iPad/iPhone can't display PDFs inside the app. Open it in the
                    browser's PDF viewer instead.
                  </p>
                  <Button onClick={() => openUrlInNewTab(doc.url)}>
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Open PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => triggerDownload(doc.url, doc.fileName)}>
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              ) : (
                <PdfDocumentPreview fileUrl={doc.url} fileName={doc.fileName} />
              )}
            </div>
          )}

          {doc && kind === "spreadsheet" && (
            <SpreadsheetPreview fileUrl={doc.url} fileName={doc.fileName} />
          )}

          {doc && kind === "text" && (
            <div className="bg-muted p-4 rounded-md min-h-[200px]">
              {textError ? (
                <UnsupportedPanel doc={doc} message={textError} />
              ) : textContent === null ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <pre className="whitespace-pre-wrap text-sm font-mono">{textContent}</pre>
              )}
            </div>
          )}

          {doc && kind === "office" && !officeFailed && (
            <iframe
              key={doc.url}
              src={officeViewerUrl(doc.url)}
              className="w-full flex-1 min-h-[60vh] border-0 bg-muted"
              title={doc.fileName}
              onError={() => setOfficeFailed(true)}
            />
          )}

          {doc && kind === "office" && officeFailed && (
            <UnsupportedPanel
              doc={doc}
              message="Preview isn't available for this file right now. You can download it or open it in a new tab."
            />
          )}

          {doc && kind === "unsupported" && (
            <UnsupportedPanel
              doc={doc}
              message="Preview isn't available for this file type. You can download it or open it in a new tab."
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
